import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import {
    ChoiceGroup,
    DefaultButton,
    IconButton,
    IContextualMenuProps,
    Label,
    Modal,
    PrimaryButton,
    Separator,
    Shimmer,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react"
import {contentStyles, iconButtonStyles} from "./index"
import {useBranding} from "../../hooks/useBranding"
import ApprovalService, {
    IDeleteApprovalActionResponseRequest,
    IGetApprovalActionResponse,
    IGetApprovalActionResponseRequest,
    IGetApprovalActionResponseResponse,
    IGetApprovalStepResponse,
    IPostApprovalActionResponseActions,
    IPostApprovalActionResponseRequest
} from "../../services/Approval"
import {Controller, FormProvider, useForm} from 'react-hook-form'
import StatusMessage, {StatusEnum} from "../../share/StatusMessage"
import {RHFCheckBoxControl} from "../RHFControls/RHFChekBoxControl";
import CustomDialog from "../../share/CustomDialog";
import {decode} from 'html-entities'


type Properties = {
    action: IGetApprovalActionResponse | null
    currentStep: IGetApprovalStepResponse | null
    steps: IGetApprovalStepResponse[]
    onDismissModal: (update: boolean) => void
    showModal: boolean
    templateId: string
}

export function ApprovalActionResponseEdit({
                                               action,
                                               currentStep,
                                               steps,
                                               onDismissModal,
                                               showModal,
                                               templateId
                                           }: Properties) {

    const {t} = useTranslation(['approval', 'common'])
    const myForm = useForm()
    const {branding} = useBranding()

    const [commandSelectedKey, setCommandSelectedKey] = useState<string | undefined>(undefined)
    const [isErrorOnLoad, setIsErrorOnLoad] = useState(false)
    const [isLoadedData, setIsLoadedData] = useState(false)
    const [isLoadingData, setIsLoadingData] = useState(true)
    const [isNewRecord, setIsNewRecord] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [message, setMessage] = useState("")
    const [showDeleteActionResponse, setShowDeleteActionResponse] = useState(false)
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessageEnum, setStatusMessageEnum] = useState<StatusEnum>(StatusEnum.Info)


    useEffect(() => {
        if (showModal) {
            onLoad().then()
        }
    }, [showModal])

    /**
     * show Status Message
     * @param message
     * @param status
     */
    const showStatusMessage = (message: string, status: StatusEnum) => {
        setMessage(decode(message))
        setStatusMessageEnum(status)
        setShowMessage(true)
    }

    /**
     * On Before dismiss
     */
    const onBeforeDismissAction = () => {
        onDismissModal(false)
    }

    const onLoad = async () => {

        await initLoad().then()
    }

    /**
     * Load init
     */
    const initLoad = async () => {
        setMessage("")
        setShowMessage(false)
        setIsErrorOnLoad(false)
        setIsLoadingData(true)
        setIsLoadedData(false)
        setIsNewRecord(true)

        myForm.clearErrors()
        myForm.reset()

        const request: IGetApprovalActionResponseRequest = {
            TemplateId: templateId,
            Mode: 'approvalStepActionId',
            Id: String(action?.Id ?? '0')
        }

        await ApprovalService.getApprovalActionResponse(request)
            .then(data => {
                if (data.Success) {
                    const resultData = JSON.parse(data.Result) as Array<IGetApprovalActionResponseResponse>

                    if (Array.isArray(resultData)) {

                        if (resultData.length > 0) {

                            setIsNewRecord(false)

                            const commandItem = resultData.find(item => item.DoActionType?.trim().toLowerCase() === 'command')

                            if (commandItem) {

                                switch (commandItem.DoAction.trim().toLowerCase()) {
                                    case 'nextstep':
                                        myForm.setValue("DoActionCommand", commandItem.DoAction.trim())
                                        setCommandSelectedKey(commandItem.DoAction.trim())
                                        break
                                    case 'previousstep':
                                        myForm.setValue("DoActionCommand", commandItem.DoAction.trim())
                                        setCommandSelectedKey(commandItem.DoAction.trim())
                                        break
                                    case 'abort':
                                        myForm.setValue("DoActionCommand", commandItem.DoAction.trim())
                                        setCommandSelectedKey(commandItem.DoAction.trim())
                                        break

                                    case 'complete':
                                        myForm.setValue("DoActionCommand", commandItem.DoAction.trim())
                                        setCommandSelectedKey(commandItem.DoAction.trim())
                                        break
                                    default:
                                        myForm.setValue("DoActionCommand", undefined)
                                        setCommandSelectedKey(undefined)
                                        break
                                }
                            } else {
                                myForm.setValue("DoActionCommand", undefined)
                                setCommandSelectedKey(undefined)
                            }

                            const addionalNotifys = resultData.some(item => item.DoActionType?.trim().toLowerCase() === 'notify'
                                && item.DoAction.trim().toLowerCase() === 'additionalnotifys')

                            const requestor = resultData.some(item => item.DoActionType?.trim().toLowerCase() === 'notify'
                                && item.DoAction.trim().toLowerCase() === 'requestor')

                            const previousApprovers = resultData.some(item => item.DoActionType?.trim().toLowerCase() === 'notify'
                                && item.DoAction.trim().toLowerCase() === 'previousrequestors')


                            if (addionalNotifys) {
                                myForm.setValue("DoActionNotificationEmail", true)
                            }

                            if (requestor) {
                                myForm.setValue("DoActionNotificationRequestor", true)
                            }

                            if (previousApprovers) {
                                myForm.setValue("DoActionNotificationPrevious", true)
                            }
                        } else {
                            setIsNewRecord(true)
                        }

                        setIsLoadedData(true)
                    } else {
                        showStatusMessage(t('message.notFound', {ns: 'common'}), StatusEnum.Error)
                    }
                } else {
                    showStatusMessage(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }

            })
            .catch((error) => {
                showStatusMessage(t('message.error', {ns: 'common', Error: error.message}), StatusEnum.Error)
            })
            .finally(() => {
                setIsLoadingData(false)
            })

    }


    const deleteActionResponse = async () => {

        setIsSaving(true)
        setShowMessage(false)
        setShowDeleteActionResponse(false)
        myForm.clearErrors()

        const requestKim: IDeleteApprovalActionResponseRequest = {
            TemplateId: templateId,
            Operation: 'deleteByApprovalStepActionId',
            Id: String(action?.Id) ?? '0'
        }

        await ApprovalService.deleteApprovalActionResponse(requestKim)
            .then(data => {
                if (data.Success) {
                    showStatusMessage(t('message.successRemoved', {ns: 'common'}), StatusEnum.Success)
                    onDismissModal(true)

                } else {
                    showStatusMessage(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }
            })
            .catch((error) => {
                showStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsSaving(false)
            })
    }


    const onSave = async () => {
        setIsSaving(false)
        setShowMessage(false)
        myForm.clearErrors()

        await myForm.handleSubmit(async (_) => {


                setIsSaving(true)

                const actions: IPostApprovalActionResponseActions[] = []

                actions.push({
                    DoActionType: 'command',
                    DoAction: myForm.getValues("DoActionCommand"),
                })

                if (myForm.getValues("DoActionNotificationEmail")) {
                    actions.push({
                        DoActionType: 'notify',
                        DoAction: 'AdditionalNotifys'
                    })
                }

                if (myForm.getValues("DoActionNotificationRequestor")) {
                    actions.push({
                        DoActionType: 'notify',
                        DoAction: 'Requestor'
                    })
                }

                if (myForm.getValues("DoActionNotificationPrevious")) {
                    actions.push({
                        DoActionType: 'notify',
                        DoAction: 'PreviousRequestors'
                    })
                }

                const request: IPostApprovalActionResponseRequest = {
                    OnEvent: `On${action?.Type}`,
                    ApprovalStepActionId: action?.Id ?? 0,
                    ApprovalStepId: action?.ApprovalStepId ?? 0,
                    TemplateId: templateId,
                    Actions: actions
                }

                await ApprovalService.postApprovalActionResponse(request)
                    .then(data => {
                        if (data.Success) {
                            showStatusMessage(t('message.successCreated', {ns: 'common'}), StatusEnum.Success)

                            onDismissModal(true)

                        } else {
                            showStatusMessage(t('message.error', {
                                ns: 'common',
                                Error: data.ExceptionMessage
                            }), StatusEnum.Error)
                        }
                    })
                    .catch((error) => {
                        showStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })


            }, () => {
                showStatusMessage(t("message.fieldsRequired", {ns: "common"}), StatusEnum.Error)
            }
        )()

    }


    /**
     * Submenu
     */
    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'deleteTemplateDefinition',
                text: t('Common.Button.Remove', {ns: 'common'}),
                iconProps: {iconName: 'Trash'},
                onClick: () => {
                    setShowDeleteActionResponse(true)
                }
            },
        ],
    }


    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerResponseActions}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"} id={'approvalActionEditTitle'}
                                  title={action === null ? t('title.newApprovalAction', {ns: 'approval'}) : t('title.editApprovalAction', {
                                      ns: 'approval',
                                      Name: action?.Type
                                  })}>
                                {action === null ? t('title.newApprovalAction', {ns: 'approval'}) : t('title.editApprovalAction', {
                                    ns: 'approval',
                                    Name: action?.Type
                                })}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={onBeforeDismissAction}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}} tokens={{childrenGap: 8}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">

                                    {/*region MESSAGES*/}
                                    <StackItem styles={{root: {paddingBottom: 16}}}>
                                        {showMessage &&
                                            <>
                                                <StatusMessage status={statusMessageEnum} hasTimer={false}
                                                               truncated={true}
                                                               isMultiline={true}
                                                               dismiss={true}
                                                               setShowMessage={setShowMessage}>
                                                    <div>{message}</div>
                                                </StatusMessage>
                                            </>
                                        }
                                    </StackItem>
                                    {/*endregion*/}


                                    {/*region TRY AGAIN*/}
                                    {isErrorOnLoad &&
                                        <>
                                            <Stack grow={1} styles={{
                                                root: {
                                                    height: "100%",
                                                    justifyContent: "center",
                                                    alignItems: "center"
                                                }
                                            }}>
                                                <StackItem>
                                                    <DefaultButton onClick={() => {
                                                        onLoad().then()
                                                    }} style={{marginLeft: 8}}
                                                                   text={t('Common.Button.Try', {ns: 'common'})}
                                                                   title={t('Common.Button.Try', {ns: 'common'})}/>
                                                </StackItem>
                                                <StackItem tokens={{padding: 8}}>
                                                    <Text
                                                        variant={"medium"}>{t("text.errorAndTry", {ns: "common"})}</Text>
                                                </StackItem>
                                            </Stack>
                                        </>
                                    }
                                    {/*endregion*/}

                                    {/*region CONTENT FORM*/}
                                    {!isErrorOnLoad &&

                                        <>

                                            <FormProvider {...myForm}>
                                                <form>
                                                    {isLoadingData && <>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 32}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                    </>}
                                                    {isLoadedData &&
                                                        <>


                                                            <Stack tokens={{childrenGap: 8}}
                                                                   styles={{root: {paddingBottom: 16}}}>

                                                                <Label
                                                                    title={t('title.notifications', {ns: 'approval'})}>
                                                                    {t('title.notifications', {ns: 'approval'})}
                                                                </Label>

                                                                <RHFCheckBoxControl
                                                                    id={"DoActionNotificationRequestor"}
                                                                    label={t('text.notificationRequestor', {ns: 'approval'})}
                                                                    title={t('text.notificationRequestor', {ns: 'approval'})}
                                                                    control={myForm.control}
                                                                    setValue={myForm.setValue}
                                                                    getValues={myForm.getValues}
                                                                    disabled={isSaving}
                                                                    trigger={myForm.trigger}/>

                                                                <RHFCheckBoxControl
                                                                    id={"DoActionNotificationPrevious"}
                                                                    label={t('text.notificationPrevious', {ns: 'approval'})}
                                                                    title={t('text.notificationPrevious', {ns: 'approval'})}
                                                                    control={myForm.control}
                                                                    setValue={myForm.setValue}
                                                                    getValues={myForm.getValues}
                                                                    disabled={isSaving}
                                                                    trigger={myForm.trigger}/>

                                                                <RHFCheckBoxControl
                                                                    id={"DoActionNotificationEmail"}
                                                                    label={t('text.notificationEmails', {ns: 'approval'})}
                                                                    title={t('text.notificationEmails', {ns: 'approval'})}
                                                                    control={myForm.control}
                                                                    setValue={myForm.setValue}
                                                                    getValues={myForm.getValues}
                                                                    disabled={isSaving}
                                                                    trigger={myForm.trigger}/>


                                                            </Stack>

                                                            <Stack tokens={{childrenGap: 8}}>

                                                                <Label required={true}
                                                                       title={t('title.commands', {ns: 'approval'})}>
                                                                    {t('title.commands', {ns: 'approval'})}</Label>

                                                                <Controller name="DoActionCommand"
                                                                            control={myForm.control}
                                                                            rules={{required: t('text.fieldRequired', {ns: 'common'})}}
                                                                            render={({
                                                                                         field,
                                                                                         fieldState: {error}
                                                                                     }) =>
                                                                                <>
                                                                                    <ChoiceGroup
                                                                                        id={"DoActionCommand"}
                                                                                        label={""}
                                                                                        title={""}
                                                                                        styles={{}}
                                                                                        options={[
                                                                                            {
                                                                                                key: 'PreviousStep',
                                                                                                text: t('text.commandPrevious', {ns: 'approval'}),
                                                                                                disabled: currentStep?.Id === steps[0]?.Id,
                                                                                                iconProps: {iconName: 'DecreaseIndentArrow'}
                                                                                            },
                                                                                            {
                                                                                                key: 'NextStep',
                                                                                                text: t('text.commandAdvance', {ns: 'approval'}),
                                                                                                disabled: currentStep?.Id === steps[steps.length - 1]?.Id,
                                                                                                iconProps: {iconName: 'IncreaseIndentArrow'}
                                                                                            },
                                                                                            {
                                                                                                key: 'Complete',
                                                                                                text: t('text.commandComplete', {ns: 'approval'}),
                                                                                                iconProps: {iconName: 'StatusCircleCheckmark'}
                                                                                            },
                                                                                            {
                                                                                                key: 'Abort',
                                                                                                text: t('text.commandAbort', {ns: 'approval'}),
                                                                                                iconProps: {iconName: 'StatusCircleBlock'}
                                                                                            }
                                                                                        ]}
                                                                                        onChange={(_, option) => field.onChange(option?.key)}
                                                                                        disabled={isSaving}
                                                                                        required={true}
                                                                                        defaultSelectedKey={commandSelectedKey}

                                                                                    />

                                                                                    {error &&

                                                                                        <Stack styles={{
                                                                                            root: {
                                                                                                paddingTop: 8,
                                                                                                paddingBottom: 8
                                                                                            }
                                                                                        }}>
                                                                                            <Text
                                                                                                variant={"smallPlus"}
                                                                                                styles={{root: {color: branding.theme.semanticColors.errorText}}}>
                                                                                                {error.message}
                                                                                            </Text>
                                                                                        </Stack>
                                                                                    }
                                                                                </>
                                                                            }
                                                                />


                                                            </Stack>


                                                        </>
                                                    }
                                                </form>
                                            </FormProvider>


                                        </>
                                    }


                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>

                                    <Stack grow={1}>
                                        {" "}
                                    </Stack>

                                    {isSaving &&
                                        <>
                                            <StackItem>
                                                <Spinner size={SpinnerSize.medium} labelPosition="left"
                                                         label={t('text.spinnerSaving', {ns: 'common'})}/>
                                            </StackItem>
                                        </>
                                    }

                                    <Stack>

                                        {action !== null && <>

                                            {isNewRecord &&
                                                <PrimaryButton disabled={!isLoadedData || isSaving || isLoadingData}
                                                               style={{marginLeft: 8}}
                                                               onClick={() => {
                                                                   onSave().then()
                                                               }}
                                                               text={t('Common.Button.Save', {ns: 'common'})}
                                                               title={t('Common.Button.Save', {ns: 'common'})}/>
                                            }

                                            {!isNewRecord &&
                                                <PrimaryButton disabled={!isLoadedData || isSaving || isLoadingData}
                                                               style={{marginLeft: 8}}
                                                               onClick={() => {
                                                                   onSave().then()
                                                               }}
                                                               split
                                                               menuProps={menuProps}
                                                               text={t('Common.Button.Update', {ns: 'common'})}
                                                               title={t('Common.Button.Update', {ns: 'common'})}/>
                                            }


                                        </>
                                        }
                                        {action === null &&
                                            <PrimaryButton disabled={!isLoadedData || isSaving || isLoadingData}
                                                           style={{marginLeft: 8}}
                                                           onClick={() => {
                                                               onSave().then()
                                                           }}

                                                           text={t('Common.Button.Save', {ns: 'common'})}
                                                           title={t('Common.Button.Save', {ns: 'common'})}/>
                                        }
                                    </Stack>

                                    <DefaultButton
                                        onClick={() => {
                                            onBeforeDismissAction()
                                        }}
                                        styles={{root: {marginRight: 8}}}
                                        text={t('Common.Button.Close', {ns: 'common'})}
                                        title={t('Common.Button.Close', {ns: 'common'})}/>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>


            <CustomDialog title={t('title.deleteActionResponse', {ns: 'approval', Name: action?.Type ?? ''})}
                          titleId={`removeActionResponse${templateId}`}
                          actionText={t('Common.Button.Delete', {ns: 'common'})}
                          confirm={deleteActionResponse}
                          close={() => {
                              setShowDeleteActionResponse(false)
                          }}
                          isOpen={showDeleteActionResponse}
            >
                <Trans>
                    {t('text.confirmRemoveActionResponse', {ns: 'approval'})}
                </Trans>
            </CustomDialog>

        </>
    )
}


